import { useState } from 'react';

export default function HeroSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    injury: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        injury: formData.injury,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6riisja90d3mf3egvbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', injury: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/4b45583aa1692f516c4306e65e048d82.jpg"
          alt="Legal consultation"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Injured At Work<br />
              In Ohio?<br />
              <span className="text-teal-400">Speak With A Workers'<br />Compensation Lawyer.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Get the benefits you deserve. Free consultation with experienced workers' compensation attorneys serving Northeast Ohio.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:2166429007"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-full text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2 text-lg"></i>
                Call Now: 216-642-9007
              </a>
              <a
                href="#consultation-form"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-full text-base font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                Free Case Review
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div id="consultation-form" className="bg-white rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Your Free Consultation</h3>
              <p className="text-sm text-gray-600 mb-6">No obligation. Confidential. We come to you.</p>
              
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(216) 555-0123"
                    />
                  </div>
                  <div>
                    <label htmlFor="injury" className="block text-sm font-medium text-gray-700 mb-1">
                      Brief Description of Injury *
                    </label>
                    <textarea
                      id="injury"
                      name="injury"
                      required
                      rows={4}
                      maxLength={500}
                      value={formData.injury}
                      onChange={(e) => setFormData({ ...formData, injury: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us what happened..."
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.injury.length}/500 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-gray-900 text-white rounded-lg text-base font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Your Free Consultation'}
                  </button>
                </div>
              </form>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <p className="text-sm text-teal-800 font-medium">Thank you! We'll contact you shortly.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">Something went wrong. Please call us at 216-642-9007.</p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-6 text-xs text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-shield-check-line text-teal-600 mr-1"></i>
                    <span>Confidential</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-time-line text-teal-600 mr-1"></i>
                    <span>Quick Response</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-money-dollar-circle-line text-teal-600 mr-1"></i>
                    <span>No Fee Unless We Win</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}